<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { DOCUMENT_TYPE, type DocumentStatus, type DocumentType } from '@hsl-ems/contracts';

import { p1Api } from '@/lib/p1-api';
import { p2Api, type DocumentRecord, type OperatingOrganization } from '@/lib/p2-api';

const router = useRouter();
const loading = ref(true);
const error = ref('');
const documents = ref<DocumentRecord[]>([]);
const organizations = ref<OperatingOrganization[]>([]);
const query = ref('');
const typeFilter = ref('');
const statusFilter = ref('');
const canCreate = ref(false);
const canApprove = ref(false);

const types: Array<{ value: DocumentType; label: string; slug: string }> = [
  { value: DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT, label: '费用报销', slug: 'reimbursement' },
  { value: DOCUMENT_TYPE.LOAN, label: '借款申请', slug: 'loan' },
  { value: DOCUMENT_TYPE.CASH_REPAYMENT, label: '现金还款', slug: 'cash-repayment' },
  { value: DOCUMENT_TYPE.CORPORATE_PAYMENT, label: '对公付款', slug: 'corporate-payment' },
];

const statusLabels: Record<DocumentStatus, string> = {
  DRAFT: '草稿',
  IN_APPROVAL: '审批中',
  RETURNED: '已退回',
  REJECTED: '已驳回',
  APPROVED: '已批准',
  COMPLETED: '已完成',
  VOIDED: '已作废',
  CORRECTED: '已更正',
};

const filtered = computed(() => {
  const needle = query.value.trim().toLowerCase();
  return documents.value.filter((document) => {
    if (typeFilter.value && document.type !== typeFilter.value) return false;
    if (statusFilter.value && document.status !== statusFilter.value) return false;
    return (
      !needle ||
      document.documentNo.toLowerCase().includes(needle) ||
      document.description.toLowerCase().includes(needle)
    );
  });
});

onMounted(load);

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const me = await p1Api.me();
    if (me.mustChangePassword) return void (await router.replace('/change-password'));
    canCreate.value = me.permissions.includes('document:create:self');
    canApprove.value = me.permissions.includes('approval:decide');
    const [reimbursements, loans, repayments, corporatePayments] = await Promise.all([
      p2Api.list(DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT),
      p2Api.list(DOCUMENT_TYPE.LOAN),
      p2Api.list(DOCUMENT_TYPE.CASH_REPAYMENT),
      p2Api.list(DOCUMENT_TYPE.CORPORATE_PAYMENT),
    ]);
    documents.value = [...reimbursements, ...loans, ...repayments, ...corporatePayments].sort(
      (left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt),
    );
    if (canCreate.value) organizations.value = await p2Api.operatingOrganizations();
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : '单据加载失败';
    if (message.includes('登录已失效')) return void (await router.replace('/login'));
    error.value = message;
  } finally {
    loading.value = false;
  }
}

function typeLabel(type: DocumentType) {
  return types.find((item) => item.value === type)?.label ?? type;
}

function documentPath(document: DocumentRecord) {
  const slug = types.find((item) => item.value === document.type)?.slug;
  return slug ? `/documents/${slug}/${document.id}` : '/documents';
}

function organizationName(id: string) {
  return organizations.value.find((item) => item.id === id)?.name ?? '已授权组织';
}

function money(value: string) {
  return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(
    Number(value),
  );
}

function dateTime(value: string) {
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
</script>

<template>
  <main class="workspace-shell">
    <header class="workspace-header">
      <RouterLink class="workspace-brand" to="/documents">
        <span class="brand-mark" aria-hidden="true">郎</span>
        <span><strong>郎报通</strong><small>个人工作台</small></span>
      </RouterLink>
      <nav aria-label="主导航">
        <RouterLink class="active" to="/documents">我的单据</RouterLink>
        <RouterLink v-if="canApprove" to="/approval-tasks">审批待办</RouterLink>
        <RouterLink to="/admin">组织与权限</RouterLink>
      </nav>
      <RouterLink class="text-link" to="/login">切换账号</RouterLink>
    </header>

    <section class="page-heading">
      <div>
        <p class="eyebrow">P2 · 单据与附件</p>
        <h1>我的单据</h1>
        <p>查看本人或当前数据范围内的申请，草稿会显式保存。</p>
      </div>
      <details v-if="canCreate" class="create-menu">
        <summary>新建申请</summary>
        <RouterLink v-for="type in types" :key="type.value" :to="`/documents/new/${type.slug}`">
          {{ type.label }}
        </RouterLink>
      </details>
    </section>

    <section class="filter-bar" aria-label="单据筛选">
      <label>
        <span>搜索</span>
        <input v-model="query" placeholder="编号或事由" />
      </label>
      <label>
        <span>类型</span>
        <select v-model="typeFilter">
          <option value="">全部类型</option>
          <option v-for="type in types" :key="type.value" :value="type.value">
            {{ type.label }}
          </option>
        </select>
      </label>
      <label>
        <span>状态</span>
        <select v-model="statusFilter">
          <option value="">全部状态</option>
          <option v-for="(label, status) in statusLabels" :key="status" :value="status">
            {{ label }}
          </option>
        </select>
      </label>
      <button class="quiet-button" type="button" :disabled="loading" @click="load">刷新</button>
    </section>

    <p v-if="error" class="inline-alert inline-alert--error" role="alert">{{ error }}</p>
    <section v-if="loading" class="document-empty" aria-live="polite">正在加载单据…</section>
    <section v-else-if="!filtered.length" class="document-empty">
      <strong>暂无符合条件的单据</strong>
      <p>{{ canCreate ? '可以从“新建申请”开始保存第一张草稿。' : '当前权限范围内没有单据。' }}</p>
    </section>
    <section v-else class="document-list" aria-label="单据列表">
      <RouterLink
        v-for="document in filtered"
        :key="document.id"
        class="document-row"
        :to="documentPath(document)"
      >
        <span class="document-icon">{{ typeLabel(document.type).slice(0, 1) }}</span>
        <span class="document-main">
          <span class="document-title">
            <strong>{{ document.documentNo }}</strong>
            <span class="status-pill" :data-status="document.status">
              {{ statusLabels[document.status] }}
            </span>
          </span>
          <span
            >{{ typeLabel(document.type) }} · {{ organizationName(document.operatingOrgId) }}</span
          >
          <small>{{ document.description }} · 更新于 {{ dateTime(document.updatedAt) }}</small>
        </span>
        <strong class="document-amount">{{ money(document.requestedAmount) }}</strong>
        <span aria-hidden="true">›</span>
      </RouterLink>
    </section>

    <nav class="mobile-nav" aria-label="移动端主导航">
      <RouterLink class="active" to="/documents">单据</RouterLink>
      <RouterLink v-if="canApprove" to="/approval-tasks">审批</RouterLink>
      <RouterLink to="/admin">我的</RouterLink>
    </nav>
  </main>
</template>
